import {
  Badge,
  Button,
  Spinner,
  Tooltip,
} from "@chakra-ui/react";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BsThreeDotsVertical } from "react-icons/bs";
import Card from "components/card";
import AppModal from "components/modal/AppModal";
import PdfModal from "components/modal/PdfModal";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { dteDocumentsApi,
  dteDocumentPaymentsApi,
} from "api/endpoints";
import { useShallow } from "zustand/react/shallow";
import { useAuth } from "context/AuthContext";
import ReenviarCorreoModal from "./ReenviarCorreoModal";
import { useEmissionAmbienteStore } from "stores/emissionAmbienteStore";
import { DTE_TIPO_FILTER_SELECT_OPTIONS, labelTipoDte } from "utils/cafXmlPreview";
import ColumnSortHeader from "./ColumnSortHeader";
import { useIdioma } from "stores/idiomaStore";
import ServerTablePagination from "./ServerTablePagination";
import FiltroRangoFechas from "components/filters/FiltroRangoFechas";
import { DteDocumentDetailFields } from "./DteDocumentDetailFields";
import {
  canalDeVenta,
  pasarelaAparte,
  NOMBRE_DE_CANAL,
  PASARELA_DE_CANAL,
} from "utils/canalDeVenta";
import {
  isDocumentRepaired,
  resolveRepairReference,
} from "utils/reparacionDte";

const columnHelper = createColumnHelper();

function formatEmissionDate(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "string") {
    const raw = value.trim();
    const pureDateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (pureDateMatch) {
      const [, year, month, day] = pureDateMatch;
      const local = new Date(Number(year), Number(month) - 1, Number(day));
      if (!Number.isNaN(local.getTime())) {
        return local.toLocaleDateString("es-CL");
      }
    }
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-CL", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function formatEmissionDateTime(value, row = {}) {
  if (value == null || value === "") return "—";
  if (typeof value === "string") {
    const raw = value.trim();
    const longDateTimeMatch = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(raw);
    if (longDateTimeMatch) {
      const [, year, month, day, hour, minute, second = "00"] = longDateTimeMatch;
      const local = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
      );
      if (!Number.isNaN(local.getTime())) {
        return local.toLocaleString("es-CL", {
          dateStyle: "short",
          timeStyle: "short",
        });
      }
    }
    const pureDateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
    if (pureDateMatch) {
      const baseDate = formatEmissionDate(raw);
      const createdAt = row?.created_at;
      const created = createdAt ? new Date(createdAt) : null;
      if (created && !Number.isNaN(created.getTime())) {
        const timePart = created.toLocaleTimeString("es-CL", {
          hour: "numeric",
          minute: "2-digit",
        });
        return `${baseDate}, ${timePart}`;
      }
      return baseDate;
    }
  }
  return formatEmissionDate(value);
}

function resolveEmissionDate(row = {}) {
  const pn = row?.payload_normalized;
  const po = row?.payload_original;
  const responseMeta = row?.response_snapshot?.meta;
  const candidates = [
    row?.fchEmisLong,
    po?.fchEmisLong,
    po?.factura?.fchEmisLong,
    po?.boleta?.fchEmisLong,
    po?.notaCredito?.fchEmisLong,
    po?.notaDebito?.fchEmisLong,
    po?.guiaDespacho?.fchEmisLong,
    pn?.fchEmisLong,
    pn?.factura?.fchEmisLong,
    pn?.boleta?.fchEmisLong,
    pn?.notaCredito?.fchEmisLong,
    pn?.notaDebito?.fchEmisLong,
    pn?.guiaDespacho?.fchEmisLong,
    row?.fch_emis,
    row?.fecha_emision,
    row?.emis_date,
    pn?.fch_emis,
    pn?.fecha_emision,
    pn?.fechaEmision,
    pn?.FchEmis,
    pn?.Encabezado?.IdDoc?.FchEmis,
    pn?.encabezado?.idDoc?.fchEmis,
    po?.fch_emis,
    po?.fecha_emision,
    po?.fechaEmision,
    po?.FchEmis,
    po?.Encabezado?.IdDoc?.FchEmis,
    po?.encabezado?.idDoc?.fchEmis,
    responseMeta?.tmstFirmaEnv,
  ];
  for (const value of candidates) {
    if (value == null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    return value;
  }
  return null;
}

function formatMoney(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(n);
}

/** Máximo de caracteres visibles en receptor (referencia: «Sociedad Comercial Mancilla »). */
const RECEPTOR_MAX_CHARS = "Sociedad Comercial Mancilla ".length;

function truncateChars(text, maxLen) {
  if (text == null || text === "") return "—";
  const s = String(text);
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen)}…`;
}

/**
 * `colorScheme` de Chakra UI Badge (doc Horizon / Chakra).
 * @see https://horizon-ui.com/documentation/docs/data-display/badge
 */
function lifecycleStatusColorScheme(status) {
  const key = String(status ?? "").trim().toLowerCase();
  const map = {
    rendered: "gray",
    sent: "blue",
    accepted: "green",
    accepted_with_observations: "yellow", // Aceptado con Reparos: valido, con observaciones SII
    rejected: "pink",
    reparado: "purple",
    cancelled: "orange",
    error: "red",
  };
  return map[key] || "gray";
}

/**
 * Etiqueta legible para el badge. Para "accepted_with_observations" mostramos
 * "Aceptado" (es un documento valido) con una marca de observacion, ya que el
 * SII lo acepto con reparos leves. El texto crudo se usa como fallback.
 */
function lifecycleStatusLabel(status) {
  const key = String(status ?? "").trim().toLowerCase();
  const map = {
    accepted_with_observations: "Aceptado ⚠",
  };
  return map[key] || String(status ?? "");
}

/** Tooltip explicativo por estado (vacio = sin tooltip). */
function lifecycleStatusTooltip(status) {
  const key = String(status ?? "").trim().toLowerCase();
  const map = {
    accepted_with_observations: "Aceptado con reparos: el SII acepto el documento con observaciones leves. Es valido y NO debe reemitirse.",
  };
  return map[key] || "";
}

/** Filtros admitidos por GET /api/v1/dte-documents (backend). */
const LIST_FILTERS_INITIAL = () => ({
  modo: "",
  lifecycle_status: "",
  tipo_dte: "",
  rut_emisor: "",
  limit: 100,
  sort_by: "created_at",
  sort_dir: "desc",
});

const EXPORT_LIMIT_OPTIONS = [1000, 2500, 5000, 10000];

// Etiquetas como claves del diccionario, igual que MODO_OPTIONS.
const LIFECYCLE_OPTIONS = [
  { value: "", label: "opcion.todosEstados" },
  { value: "rendered", label: "estado.generado" },
  { value: "sent", label: "estado.enviado" },
  { value: "accepted", label: "estado.aceptado" },
  { value: "accepted_with_observations", label: "estado.reparos" },
  { value: "rejected", label: "estado.rechazado" },
  { value: "cancelled", label: "estado.anulado" },
  { value: "error", label: "estado.error" },
];

// Etiquetas como claves del diccionario: se traducen al pintarlas. El selector
// aparece deshabilitado para los roles restringidos, pero igual se lee.
const MODO_OPTIONS = [
  { value: "", label: "filtro.todosModos" },
  { value: "certificacion", label: "sii.certificacion" },
  { value: "produccion", label: "sii.produccion" },
];

const LIMIT_OPTIONS = [50, 100, 200];
const REENQUEUE_COOLDOWN_MS = 90 * 1000;
const REENQUEUE_TS_STORAGE_PREFIX = "dte_reenqueue_started_at_";

function reenqueueStorageKey(id) {
  return `${REENQUEUE_TS_STORAGE_PREFIX}${String(id).trim()}`;
}

function readReenqueueStartedAtFromStorage(id) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(reenqueueStorageKey(id));
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
  } catch {
    return null;
  }
}

function writeReenqueueStartedAtToStorage(id, startedAtTs) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      reenqueueStorageKey(id),
      String(Math.max(0, Number(startedAtTs) || 0)),
    );
  } catch {
    // Ignorar errores de localStorage.
  }
}

function formatElapsedTimer(ms) {
  const safe = Math.max(0, Math.floor(Number(ms) || 0));
  const totalSeconds = Math.floor(safe / 1000);
  const mm = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
  const ss = String(totalSeconds % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function hasPdfDocument(row) {
  return Boolean(String(row?.pdf_url || "").trim());
}

function hasSiiTrack(row) {
  return row?.sii_track_id != null && String(row.sii_track_id).trim() !== "";
}

function downloadTextFile({ text, filename, type }) {
  const blob = new Blob([text], { type });
  downloadBlob({ blob, filename });
}

function downloadBlob({ blob, filename }) {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

function getDocumentLifecycleDisplayStatus(row) {
  if (isDocumentRepaired(row)) return "reparado";
  return String(row?.lifecycle_status ?? "").trim().toLowerCase();
}

// Fechas ISO locales (YYYY-MM-DD) para el filtro de rango por fecha de emisión.
function isoTodayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function isoMonthStartLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

export default function DteDocumentsTable() {
  const location = useLocation();
  const navigate = useNavigate();
  const { puedeReenviarDocumentos, puedeGestionarDocumentos } = useAuth();
  const didHydrateFromUrlRef = React.useRef(false);
  const emission = useEmissionAmbienteStore(
    useShallow((s) => ({
      kind: s.kind,
      ambiente: s.ambiente,
      profileUuid: s.profileUuid,
      rutEmisor: s.rutEmisor,
    })),
  );
  const selectedAmbiente =
    emission.kind === "ready" && emission.ambiente ? emission.ambiente : "";
  const selectedProfileUuid =
    emission.kind === "ready" && emission.profileUuid ? String(emission.profileUuid).trim() : "";
  const selectedRutEmisor =
    emission.kind === "ready" && emission.rutEmisor ? String(emission.rutEmisor).trim() : "";
  const repairedOnly = String(new URLSearchParams(location.search || "").get("repaired") || "")
    .trim()
    .toLowerCase() === "true";
  const [showHidden, setShowHidden] = React.useState(false);
  const [listFilters, setListFilters] = React.useState(LIST_FILTERS_INITIAL);
  // Rango de fechas por emisión. Por defecto: mes actual (inicio de mes → hoy).
  const [dateDesde, setDateDesde] = React.useState(isoMonthStartLocal);
  const [dateHasta, setDateHasta] = React.useState(isoTodayLocal);
  const [page, setPage] = React.useState(1);
  /** Texto del input de búsqueda (API `q`, servidor). */
  const [searchInput, setSearchInput] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  const [data, setData] = React.useState([]);
  // Info de pago de las filas visibles. Se pide aparte del listado para no
  // cargar con joins el camino mas usado del panel.
  const [pagosPorDoc, setPagosPorDoc] = React.useState({});
  const [pagoAbierto, setPagoAbierto] = React.useState(null);
  const [listMeta, setListMeta] = React.useState({
    total: 0,
    total_pages: 0,
    page: 1,
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [exportLimit, setExportLimit] = React.useState(5000);
  const [exporting, setExporting] = React.useState(false);
  const [exportMessage, setExportMessage] = React.useState("");
  const [enqueueBusyById, setEnqueueBusyById] = React.useState({});
  const [repairBusyById, setRepairBusyById] = React.useState({});
  const [hideBusyById, setHideBusyById] = React.useState({});
  const [enqueueMessage, setEnqueueMessage] = React.useState("");
  const [reenqueueStartedAtById, setReenqueueStartedAtById] = React.useState({});
  const [clockNow, setClockNow] = React.useState(() => Date.now());
  const [openActionsMenuId, setOpenActionsMenuId] = React.useState(null);
  // Hacia dónde se abre el menú de acciones.
  //
  // La tarjeta lleva `sm:overflow-x-auto`, y en CSS fijar un eje del overflow
  // convierte el otro en `auto`: la tarjeta también recorta en vertical. En la
  // última fila el menú quedaba cortado por abajo y la opción de enviar al
  // cliente era inalcanzable.
  const [menuHaciaArriba, setMenuHaciaArriba] = React.useState(false);

  /** Abre el menú hacia donde quepa, midiendo el espacio real bajo el botón. */
  const alternarMenuAcciones = React.useCallback((key, evento) => {
    setOpenActionsMenuId((actual) => {
      if (actual === key) return null;
      const caja = evento?.currentTarget?.getBoundingClientRect?.();
      // Alto aproximado con todas las opciones visibles. Pasarse es inofensivo:
      // solo hace que se abra hacia arriba un poco antes de lo necesario.
      const ALTO_MENU = 230;
      setMenuHaciaArriba(
        Boolean(caja) && window.innerHeight - caja.bottom < ALTO_MENU && caja.top > ALTO_MENU,
      );
      return key;
    });
  }, []);

  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailId, setDetailId] = React.useState(null);
  const [detailItem, setDetailItem] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailError, setDetailError] = React.useState("");

  const [pdfOpen, setPdfOpen] = React.useState(false);
  const [pdfUrl, setPdfUrl] = React.useState("");
  const [pdfTitle, setPdfTitle] = React.useState("");

  const closePdf = React.useCallback(() => {
    setPdfOpen(false);
    setPdfUrl("");
    setPdfTitle("");
  }, []);

  const openPdf = React.useCallback((url, label) => {
    if (!url) return;
    setPdfUrl(url);
    setPdfTitle(
      label != null && String(label).trim() !== ""
        ? `PDF · ${String(label).trim()}`
        : "Vista previa PDF"
    );
    setPdfOpen(true);
  }, []);

  // El PDF del documento que reemplazó a este.
  //
  // La fila solo guarda el id del reemplazo, no su pdf_url, así que hay que
  // pedirlo. Se pide al hacer clic y no al listar: son pocas filas reparadas y
  // traer el PDF de cada reemplazo al cargar la tabla sería una consulta por
  // fila para algo que casi nadie abre.
  const [pdfReemplazoBusyId, setPdfReemplazoBusyId] = React.useState(null);

  const cargarDocumento = React.useCallback(async (id) => {
    try {
      const { res, data } = await dteDocumentsApi.get(String(id));
      if (!res.ok || !data?.ok) return null;
      return data.item ?? null;
    } catch {
      return null;
    }
  }, []);

  const abrirPdfDeReemplazo = React.useCallback(async (idReemplazo) => {
    if (idReemplazo == null) return;
    setPdfReemplazoBusyId(String(idReemplazo));
    try {
      const item = await cargarDocumento(idReemplazo);
      if (!item?.pdf_url) {
        window.alert("No se pudo abrir el PDF del documento que reparó a este.");
        return;
      }
      openPdf(
        item.pdf_url,
        item.sii_track_id != null && item.sii_track_id !== "" ? item.sii_track_id : item.folio,
      );
    } finally {
      setPdfReemplazoBusyId(null);
    }
  }, [openPdf, cargarDocumento]);

  // Mandarle al cliente el documento que reparó a este.
  //
  // El modal de reenvío necesita el documento entero y la fila solo guarda el
  // id del reemplazo, así que se trae acá. De paso se comprueba su estado: el
  // backend solo deja enviar los aceptados, y es mejor decir por qué no se
  // puede que mostrar un modal que después falla.
  const [reenvioReemplazoBusyId, setReenvioReemplazoBusyId] = React.useState(null);
  const abrirReenvioDeReemplazo = React.useCallback(async (idReemplazo) => {
    if (idReemplazo == null) return;
    setReenvioReemplazoBusyId(String(idReemplazo));
    try {
      const item = await cargarDocumento(idReemplazo);
      if (!item) {
        window.alert("No se pudo cargar el documento que reparó a este.");
        return;
      }
      const estado = String(item.lifecycle_status ?? "").trim().toLowerCase();
      if (estado !== "accepted" && estado !== "accepted_with_observations") {
        window.alert(
          `El documento que reparó a este todavía no está aceptado por el SII (${estado || "sin estado"}). `
            + "Solo se le puede enviar al cliente una vez aceptado.",
        );
        return;
      }
      setReenvioDoc(item);
    } finally {
      setReenvioReemplazoBusyId(null);
    }
  }, [cargarDocumento]);

  const closeDetail = React.useCallback(() => {
    setDetailOpen(false);
    setDetailId(null);
    setDetailItem(null);
    setDetailError("");
  }, []);

  const openDetail = React.useCallback(async (id) => {
    setDetailId(id);
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError("");
    setDetailItem(null);
    try {
      const { res, data } = await dteDocumentsApi.get(String(id));
      if (!res.ok || !data?.ok) {
        setDetailError(data?.error || `Error ${res.status}`);
        return;
      }
      setDetailItem(data.item ?? null);
    } catch (e) {
      setDetailError(e instanceof Error ? e.message : "Error de red.");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (didHydrateFromUrlRef.current) return;
    didHydrateFromUrlRef.current = true;
    const sp = new URLSearchParams(location.search || "");
    const repairedParam = String(sp.get("repaired") || "").trim().toLowerCase();
    const lifecycleParam = String(sp.get("lifecycle_status") || "")
      .trim()
      .toLowerCase();
    const tipoDteParam = String(sp.get("tipo_dte") || "").trim();
    const qParam = String(sp.get("q") || "").trim();

    if (lifecycleParam && repairedParam !== "true") {
      const allowed = new Set(
        LIFECYCLE_OPTIONS.map((o) => String(o.value || "").trim().toLowerCase()).filter(Boolean)
      );
      if (allowed.has(lifecycleParam)) {
        setListFilters((f) => ({ ...f, lifecycle_status: lifecycleParam }));
      }
    }
    if (tipoDteParam && /^\d+$/.test(tipoDteParam)) {
      setListFilters((f) => ({ ...f, tipo_dte: tipoDteParam }));
    }
    if (qParam) {
      setSearchInput(qParam);
      setDebouncedSearch(qParam);
    }
  }, [location.search]);

  const openDocumentsTab = React.useCallback(
    (nextRepairedOnly) => {
      const sp = new URLSearchParams(location.search || "");
      if (nextRepairedOnly) {
        sp.set("repaired", "true");
        sp.delete("lifecycle_status");
      } else {
        sp.delete("repaired");
      }
      const qs = sp.toString();
      navigate(`/admin/data-tables${qs ? `?${qs}` : ""}`);
    },
    [location.search, navigate]
  );

  React.useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  React.useEffect(() => {
    setPage(1);
  }, [listFilters, debouncedSearch]);

  React.useEffect(() => {
    if (!selectedAmbiente) return;
    setListFilters((f) => {
      if (f.modo === selectedAmbiente) return f;
      return { ...f, modo: selectedAmbiente };
    });
  }, [selectedAmbiente]);

  React.useEffect(() => {
    if (!selectedRutEmisor) return;
    setListFilters((f) => {
      if (String(f.rut_emisor || "").trim() === selectedRutEmisor) return f;
      return { ...f, rut_emisor: selectedRutEmisor };
    });
  }, [selectedRutEmisor]);

  const fetchDocuments = React.useCallback(async () => {
    if (!selectedAmbiente) {
      setLoading(false);
      setData([]);
      setListMeta({ total: 0, total_pages: 0, page: 1 });
      return;
    }
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams();
      qs.set(
        "limit",
        String(
          Math.min(
            200,
            Math.max(1, Number(listFilters.limit) || 100)
          )
        )
      );
      const modo = String(selectedAmbiente).trim();
      if (modo) qs.set("modo", modo);
      if (selectedProfileUuid) qs.set("profileUuid", selectedProfileUuid);
      const lc = String(listFilters.lifecycle_status ?? "").trim();
      if (lc && !repairedOnly) qs.set("lifecycle_status", lc);
      if (repairedOnly) qs.set("repaired", "true");
      if (showHidden) qs.set("hidden", "true");
      const tipo = String(listFilters.tipo_dte ?? "").trim();
      if (tipo !== "") qs.set("tipo_dte", tipo);
      const rut = String(listFilters.rut_emisor ?? "").trim();
      if (rut) qs.set("rut_emisor", rut);

      if (dateDesde) qs.set("fch_emis_desde", dateDesde);
      if (dateHasta) qs.set("fch_emis_hasta", dateHasta);

      qs.set("page", String(Math.max(1, page)));
      if (debouncedSearch) qs.set("q", debouncedSearch);
      qs.set("sort_by", String(listFilters.sort_by || "created_at"));
      qs.set("sort_dir", String(listFilters.sort_dir || "desc"));

      const { res, data: payload } = await dteDocumentsApi.list(`?${qs}`);
      if (!res.ok) {
        setError(
          payload?.error ||
            payload?.message ||
            `Error ${res.status} al cargar documentos.`
        );
        setData([]);
        setListMeta({ total: 0, total_pages: 0, page: 1 });
        return;
      }
      const incomingItems = Array.isArray(payload?.items) ? payload.items : [];
      const selectedLifecycleStatus = String(listFilters.lifecycle_status || "")
        .trim()
        .toLowerCase();
      const showingRejectedOnly = !repairedOnly && selectedLifecycleStatus === "rejected";
      const showingRepairedOnly = repairedOnly;
      const visibleItems = showingRepairedOnly
        ? incomingItems.filter((row) => isDocumentRepaired(row))
        : showingRejectedOnly
          ? incomingItems.filter((row) => !isDocumentRepaired(row))
          : incomingItems;
      setData(visibleItems);

      // Si esto falla, el listado se ve igual: la columna queda en "—".
      const ids = visibleItems.map((row) => row?.id).filter(Boolean);
      setPagosPorDoc({});
      if (ids.length > 0) {
        dteDocumentPaymentsApi
          .byIds(ids)
          .then(({ res: r, data: d }) => {
            if (r?.ok && d?.items) setPagosPorDoc(d.items);
          })
          .catch(() => {});
      }

      setListMeta({
        total:
          showingRejectedOnly || showingRepairedOnly
            ? visibleItems.length
            : Number(payload?.total ?? 0),
        total_pages:
          showingRejectedOnly || showingRepairedOnly
            ? 1
            : Number(payload?.total_pages ?? 0),
        page: Number(payload?.page ?? page),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error de red.");
      setData([]);
      setListMeta({ total: 0, total_pages: 0, page: 1 });
    } finally {
      setLoading(false);
    }
  }, [listFilters, page, debouncedSearch, selectedAmbiente, selectedProfileUuid, repairedOnly, showHidden, dateDesde, dateHasta]);

  const handleExportCsv = React.useCallback(async () => {
    if (!selectedAmbiente) {
      setExportMessage("No hay ambiente emisor disponible para exportar.");
      return;
    }
    setExporting(true);
    setExportMessage("");
    try {
      const qs = new URLSearchParams();
      const modo = String(selectedAmbiente).trim();
      if (modo) qs.set("modo", modo);
      if (selectedProfileUuid) qs.set("profileUuid", selectedProfileUuid);
      const lc = String(listFilters.lifecycle_status ?? "").trim();
      if (lc && !repairedOnly) qs.set("lifecycle_status", lc);
      if (repairedOnly) qs.set("repaired", "true");
      const tipo = String(listFilters.tipo_dte ?? "").trim();
      if (tipo !== "") qs.set("tipo_dte", tipo);
      const rut = String(listFilters.rut_emisor ?? "").trim();
      if (rut) qs.set("rut_emisor", rut);
      if (debouncedSearch) qs.set("q", debouncedSearch);
      if (dateDesde) qs.set("fch_emis_desde", dateDesde);
      if (dateHasta) qs.set("fch_emis_hasta", dateHasta);
      qs.set("sort_by", String(listFilters.sort_by || "created_at"));
      qs.set("sort_dir", String(listFilters.sort_dir || "desc"));
      qs.set("export_limit", String(exportLimit));

      if (repairedOnly) {
        // Export Excel (.xlsx) server-side: parea documento RECHAZADO ↔ REPARADOR.
        const { res, blob, data, filename } = await dteDocumentsApi.exportRepairedXlsx(`?${qs}`);
        if (!res.ok || !blob) {
          throw new Error(
            data?.error || data?.message || `Error ${res.status} al exportar reparados.`,
          );
        }
        downloadBlob({ blob, filename: filename || "dte-reparados.xlsx" });
        const rowCount = res.headers.get("X-Export-Row-Count");
        const totalMatching = res.headers.get("X-Export-Total-Matching");
        const truncated = res.headers.get("X-Export-Truncated") === "true";
        setExportMessage(
          truncated
            ? `Excel de reparados generado: ${rowCount ?? "?"} filas (coincidencias totales ${totalMatching ?? "?"}; se aplicó el límite).`
            : `Excel de reparados generado: ${rowCount ?? "?"} filas.`,
        );
        return;
      }

      const { res, text } = await dteDocumentsApi.exportCsv(`?${qs}`);
      if (!res.ok) {
        let msg = `Error ${res.status}`;
        try {
          const j = JSON.parse(text);
          if (j?.error) msg = String(j.error);
          else if (j?.message) msg = String(j.message);
        } catch {
          if (text && text.length < 400) msg = text;
        }
        throw new Error(msg);
      }
      const cd = res.headers.get("Content-Disposition");
      let filename = "dte-documents-export.csv";
      if (cd) {
        const m = /filename="([^"]+)"/i.exec(cd);
        if (m?.[1]) filename = m[1];
      }
      downloadTextFile({
        text,
        filename,
        type: "text/csv;charset=utf-8",
      });
      const truncated = res.headers.get("X-Export-Truncated") === "true";
      const totalMatching = res.headers.get("X-Export-Total-Matching");
      const rowCount = res.headers.get("X-Export-Row-Count");
      setExportMessage(
        truncated
          ? `Archivo generado: ${rowCount ?? "?"} filas (coincidencias totales ${totalMatching ?? "?"}; se aplicó el límite de exportación).`
          : `Archivo generado: ${rowCount ?? "?"} filas.`,
      );
    } catch (e) {
      setExportMessage(e instanceof Error ? e.message : "Error al exportar.");
    } finally {
      setExporting(false);
    }
  }, [listFilters, debouncedSearch, exportLimit, selectedAmbiente, selectedProfileUuid, repairedOnly, dateDesde, dateHasta]);

  // Export Excel a NIVEL DETALLE: una fila por producto de cada DTE (para el contador).
  const handleExportDetailXlsx = React.useCallback(async () => {
    if (!selectedAmbiente) {
      setExportMessage("No hay ambiente emisor disponible para exportar.");
      return;
    }
    setExporting(true);
    setExportMessage("");
    try {
      const qs = new URLSearchParams();
      const modo = String(selectedAmbiente).trim();
      if (modo) qs.set("modo", modo);
      if (selectedProfileUuid) qs.set("profileUuid", selectedProfileUuid);
      const lc = String(listFilters.lifecycle_status ?? "").trim();
      if (lc && !repairedOnly) qs.set("lifecycle_status", lc);
      if (repairedOnly) qs.set("repaired", "true");
      const tipo = String(listFilters.tipo_dte ?? "").trim();
      if (tipo !== "") qs.set("tipo_dte", tipo);
      const rut = String(listFilters.rut_emisor ?? "").trim();
      if (rut) qs.set("rut_emisor", rut);
      if (debouncedSearch) qs.set("q", debouncedSearch);
      if (dateDesde) qs.set("fch_emis_desde", dateDesde);
      if (dateHasta) qs.set("fch_emis_hasta", dateHasta);
      qs.set("sort_by", String(listFilters.sort_by || "created_at"));
      qs.set("sort_dir", String(listFilters.sort_dir || "desc"));
      qs.set("export_limit", String(exportLimit));

      const { res, blob, data, filename } = await dteDocumentsApi.exportDetailXlsx(`?${qs}`);
      if (!res.ok || !blob) {
        throw new Error(data?.error || data?.message || `Error ${res.status} al exportar el detalle.`);
      }
      downloadBlob({ blob, filename: filename || "dte-emitidos-detalle.xlsx" });
      const rowCount = res.headers.get("X-Export-Row-Count");
      const totalMatching = res.headers.get("X-Export-Total-Matching");
      const truncated = res.headers.get("X-Export-Truncated") === "true";
      setExportMessage(
        truncated
          ? `Excel de detalle generado: ${rowCount ?? "?"} líneas de producto (de ${totalMatching ?? "?"} documentos; se aplicó el límite).`
          : `Excel de detalle generado: ${rowCount ?? "?"} líneas de producto.`,
      );
    } catch (e) {
      setExportMessage(e instanceof Error ? e.message : "Error al exportar el detalle.");
    } finally {
      setExporting(false);
    }
  }, [listFilters, debouncedSearch, exportLimit, selectedAmbiente, selectedProfileUuid, repairedOnly, dateDesde, dateHasta]);

  const resetListFiltersAndSearch = React.useCallback(() => {
    setListFilters(LIST_FILTERS_INITIAL());
    setSearchInput("");
    setDebouncedSearch("");
    setPage(1);
    setExportMessage("");
    setEnqueueMessage("");
  }, []);

  React.useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  React.useEffect(() => {
    const t = window.setInterval(() => {
      setClockNow(Date.now());
    }, 1000);
    return () => window.clearInterval(t);
  }, []);

  React.useEffect(() => {
    const closeMenu = () => setOpenActionsMenuId(null);
    const closeMenuOnEscape = (event) => {
      if (event.key === "Escape") closeMenu();
    };
    window.addEventListener("mousedown", closeMenu);
    window.addEventListener("keydown", closeMenuOnEscape);
    return () => {
      window.removeEventListener("mousedown", closeMenu);
      window.removeEventListener("keydown", closeMenuOnEscape);
    };
  }, []);

  React.useEffect(() => {
    const next = {};
    for (const row of data) {
      const id = row?.id;
      if (id == null) continue;
      const ts = readReenqueueStartedAtFromStorage(id);
      if (ts) next[String(id)] = ts;
    }
    setReenqueueStartedAtById(next);
  }, [data]);

  // Reenvío del documento al cliente (PDF + XML). El modal precarga el correo
  // que ya tiene guardado el documento y permite corregirlo.
  const [reenvioDoc, setReenvioDoc] = React.useState(null);
  const [reenvioBusy, setReenvioBusy] = React.useState(false);

  const handleReenviarCorreo = React.useCallback(
    async ({ to, cc }) => {
      const id = reenvioDoc?.id;
      if (id == null) return;
      setReenvioBusy(true);
      setEnqueueMessage("");
      try {
        const res = await dteDocumentsApi.deliverySend(String(id), {
          to,
          ...(cc ? { cc } : {}),
        });
        const ok = Boolean(res?.res?.ok && res?.data?.ok);
        setEnqueueMessage(
          ok
            ? `Documento reenviado a ${to}.`
            : `No se pudo reenviar: ${
                res?.data?.error || res?.data?.message || `Error ${res?.res?.status || "desconocido"}`
              }`,
        );
        if (ok) setReenvioDoc(null);
        await fetchDocuments();
      } catch (e) {
        setEnqueueMessage(e instanceof Error ? e.message : "Error de red al reenviar.");
      } finally {
        setReenvioBusy(false);
      }
    },
    [reenvioDoc, fetchDocuments],
  );

  const handleReenqueuePipeline = React.useCallback(
    async (id) => {
      const key = String(id);
      setEnqueueMessage("");
      setEnqueueBusyById((prev) => ({ ...prev, [key]: true }));
      try {
        const uploadRetry = await dteDocumentsApi.enqueueUploadRetry(key);
        const ok = Boolean(uploadRetry?.res?.ok && uploadRetry?.data?.ok);
        if (ok) {
          const startedAtTs = Date.now();
          writeReenqueueStartedAtToStorage(key, startedAtTs);
          setReenqueueStartedAtById((prev) => ({ ...prev, [key]: startedAtTs }));
          setClockNow(startedAtTs);
          setEnqueueMessage(
            "Documento rencolado para upload. Tracking y email se encolan automáticamente en el flujo normal cuando corresponda.",
          );
        } else {
          const msg =
            uploadRetry?.data?.error ||
            uploadRetry?.data?.reason ||
            uploadRetry?.data?.message ||
            `Error ${uploadRetry?.res?.status || "desconocido"}`;
          setEnqueueMessage(`No se pudo rencolar upload: ${msg}`);
        }
        await fetchDocuments();
      } catch (e) {
        setEnqueueMessage(
          e instanceof Error ? e.message : "Error de red al rencolar documento."
        );
      } finally {
        setEnqueueBusyById((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    },
    [fetchDocuments]
  );

  // Volver a preguntarle al SII por un documento que quedó en SENT.
  //
  // SENT significa "llegó al SII y no sabemos qué dijo". Cuando la consulta
  // automática se detiene —el SII caído, el worker reiniciado— el documento
  // se queda ahí para siempre: el proceso que reintentaba ya agotó sus
  // intentos y nada lo vuelve a mirar.
  //
  // El endpoint existía hace tiempo pero ningún botón lo llamaba. No altera
  // el documento ni habla de emisión: solo pide el estado de un trackId que
  // ya existe, así que repetirlo no tiene riesgo.
  const [trackingBusyById, setTrackingBusyById] = React.useState({});
  const handleConsultarEstado = React.useCallback(
    async (id) => {
      const key = String(id);
      setEnqueueMessage("");
      setTrackingBusyById((prev) => ({ ...prev, [key]: true }));
      try {
        const r = await dteDocumentsApi.enqueueTracking(key);
        if (r?.res?.ok && r?.data?.ok) {
          setEnqueueMessage(
            "Consulta encolada. El estado se actualiza en cuanto el SII responde; refresca en unos segundos.",
          );
        } else {
          const msg = r?.data?.error || r?.data?.reason || `Error ${r?.res?.status || "desconocido"}`;
          setEnqueueMessage(`No se pudo consultar el estado: ${msg}`);
        }
      } catch (e) {
        setEnqueueMessage(e instanceof Error ? e.message : "Error de red al consultar el estado.");
      } finally {
        setTrackingBusyById((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    },
    [],
  );

  // Los SENT de la página actual. De a uno no sirve cuando son varias páginas.
  const documentosSinVeredicto = React.useMemo(
    () =>
      data.filter(
        (d) =>
          String(d?.lifecycle_status || "").trim().toLowerCase() === "sent"
          && String(d?.sii_track_id || "").trim() !== "",
      ),
    [data],
  );

  const [consultaLoteProgreso, setConsultaLoteProgreso] = React.useState(null);
  const handleConsultarEstadoLote = React.useCallback(async () => {
    const pendientes = documentosSinVeredicto;
    if (!pendientes.length) return;
    setEnqueueMessage("");
    setConsultaLoteProgreso({ hechos: 0, total: pendientes.length });
    let fallidos = 0;
    // De a uno y en serie a propósito: encolar 50 consultas de golpe contra el
    // SII es la forma de que el SII empiece a rechazarlas.
    for (let i = 0; i < pendientes.length; i += 1) {
      try {
        const r = await dteDocumentsApi.enqueueTracking(String(pendientes[i].id));
        if (!(r?.res?.ok && r?.data?.ok)) fallidos += 1;
      } catch {
        fallidos += 1;
      }
      setConsultaLoteProgreso({ hechos: i + 1, total: pendientes.length });
    }
    setConsultaLoteProgreso(null);
    setEnqueueMessage(
      fallidos
        ? `Consultas encoladas: ${pendientes.length - fallidos} de ${pendientes.length} (${fallidos} fallaron).`
        : `${pendientes.length} consulta(s) encoladas. El estado se actualiza a medida que el SII responde.`,
    );
    await fetchDocuments();
  }, [documentosSinVeredicto, fetchDocuments]);

  const handleRepairWithCurrentCaf = React.useCallback(
    async (doc) => {
      const key = String(doc?.id ?? "");
      if (!key) return;
      const confirmed = window.confirm(
        `Se rehara el DTE ${doc?.folio ?? key} usando el CAF activo actual, con un folio nuevo, y luego se rencolara el upload.\n\nEsta accion crea un documento nuevo y deja trazabilidad sobre el rechazado.\n\n¿Continuar?`,
      );
      if (!confirmed) return;
      setEnqueueMessage("");
      setRepairBusyById((prev) => ({ ...prev, [key]: true }));
      try {
        const response = await dteDocumentsApi.repairWithCurrentCaf(key, {
          ...(selectedProfileUuid ? { profileUuid: selectedProfileUuid } : {}),
          reason: "CAF incorrecto en documento rechazado por SII",
          resendEmail: true,
          enqueueUpload: true,
        });
        const ok = Boolean(response?.res?.ok && response?.data?.ok);
        if (ok) {
          const repairedId =
            response?.data?.repairedDocument?.id ??
            response?.data?.result?.persistedDocument?.id ??
            "nuevo";
          const repairedFolio =
            response?.data?.repairedDocument?.folio ??
            response?.data?.result?.persistedDocument?.folio ??
            "nuevo";
          setEnqueueMessage(
            `Documento reparado con CAF actual. Nuevo DTE #${repairedId} con folio ${repairedFolio}; upload reencolado.`,
          );
          setData((prev) =>
            prev.map((row) =>
              String(row?.id) === key
                ? {
                    ...row,
                    notes: {
                      ...(row?.notes && typeof row.notes === "object" ? row.notes : {}),
                      caf_repair: {
                        ...((row?.notes?.caf_repair &&
                          typeof row.notes.caf_repair === "object"
                          ? row.notes.caf_repair
                          : {})),
                        repaired_with_current_caf: true,
                        repaired_at: new Date().toISOString(),
                        reason: "CAF incorrecto en documento rechazado por SII",
                        replacement_document_id: repairedId,
                        replacement_folio: repairedFolio,
                      },
                    },
                  }
                : row
            ).filter((row) =>
              String(listFilters.lifecycle_status || "").trim().toLowerCase() === "rejected"
                ? !isDocumentRepaired(row)
                : true
            ),
          );
        } else {
          const msg =
            response?.data?.error ||
            response?.data?.reason ||
            response?.data?.message ||
            `Error ${response?.res?.status || "desconocido"}`;
          setEnqueueMessage(`No se pudo reparar el DTE con CAF actual: ${msg}`);
        }
        await fetchDocuments();
      } catch (e) {
        setEnqueueMessage(
          e instanceof Error ? e.message : "Error de red al reparar el DTE con CAF actual.",
        );
      } finally {
        setRepairBusyById((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    },
    [fetchDocuments, listFilters.lifecycle_status, selectedProfileUuid],
  );

  const handleToggleHideDocument = React.useCallback(
    async (doc, hide) => {
      const key = String(doc?.id ?? "");
      if (!key) return;
      // Ocultar solo aplica a rechazados; restaurar aplica a cualquiera ya oculto.
      const lifecycleStatus = getDocumentLifecycleDisplayStatus(doc);
      if (hide && lifecycleStatus !== "rejected") return;
      if (hide) {
        const confirmed = window.confirm(
          `Se ocultará (desactivará) el DTE rechazado ${doc?.folio ?? key} (tipo ${doc?.tipo_dte ?? "?"}).\n\nNo se elimina: sale del listado pero queda en "Ver ocultos" y puedes restaurarlo cuando quieras.\n\n¿Continuar?`,
        );
        if (!confirmed) return;
      }
      setEnqueueMessage("");
      setHideBusyById((prev) => ({ ...prev, [key]: true }));
      try {
        const { res, data } = hide
          ? await dteDocumentsApi.hide(key)
          : await dteDocumentsApi.unhide(key);
        const ok = Boolean(res?.ok && (data?.ok ?? true));
        if (ok) {
          setEnqueueMessage(
            hide
              ? `DTE ${doc?.folio ?? key} ocultado. Míralo en "Ver ocultos".`
              : `DTE ${doc?.folio ?? key} restaurado.`,
          );
          // La fila sale de la vista actual (visibles no muestran ocultos y viceversa).
          setData((prev) => prev.filter((row) => String(row?.id) !== key));
        } else {
          const msg =
            data?.error ||
            data?.reason ||
            data?.message ||
            `Error ${res?.status || "desconocido"}`;
          setEnqueueMessage(
            `No se pudo ${hide ? "ocultar" : "restaurar"} el DTE: ${msg}`,
          );
        }
        await fetchDocuments();
      } catch (e) {
        setEnqueueMessage(
          e instanceof Error
            ? e.message
            : `Error de red al ${hide ? "ocultar" : "restaurar"} el DTE.`,
        );
      } finally {
        setHideBusyById((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    },
    [fetchDocuments],
  );

  const applyColumnSort = React.useCallback((sortKey, dir) => {
    setListFilters((f) => ({ ...f, sort_by: sortKey, sort_dir: dir }));
  }, []);

  // Idioma de la interfaz. `t` viene memoizada contra el idioma, así que sirve
  // como dependencia estable del useMemo de columnas.
  const { t } = useIdioma();

  const columns = React.useMemo(
    () => [
      // Identificación del documento en una sola columna.
      //
      // Tipo, folio e id de envío son el mismo tema —"qué documento es"— y en
      // tres columnas separadas obligaban a leer de izquierda a derecha para
      // armar la idea. Juntos se leen de un vistazo y liberan dos columnas.
      //
      // El folio va primero y destacado: es lo que la gente busca y lo que
      // dice por teléfono. El tipo va con su nombre y no solo el código,
      // porque "Boleta electrónica" se entiende y "39" hay que saberlo.
      //
      // Ordena por folio. Por tipo no hace falta: hay un filtro por tipo
      // arriba, que para esto sirve más que ordenar.
      columnHelper.accessor("folio", {
        id: "documento",
        header: () => (
          <ColumnSortHeader
            label="Documento"
            sortKey="folio"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => {
          const row = info.row.original || {};
          const folio = info.getValue();
          const trackId = row.sii_track_id;
          return (
            <div className="flex flex-col gap-0.5 leading-tight">
              <span className="whitespace-nowrap text-sm font-bold text-navy-700 dark:text-white">
                {folio != null && folio !== "" ? `Folio ${folio}` : "Sin folio"}
              </span>
              <span className="whitespace-nowrap text-xs text-gray-600 dark:text-gray-400">
                {row.tipo_dte != null ? labelTipoDte(row.tipo_dte) : "—"}
              </span>
              <span
                className="whitespace-nowrap font-mono text-[11px] text-gray-500 dark:text-gray-500"
                title={trackId ? `${t("sii.trackId")}: ${trackId}` : undefined}
              >
                {trackId != null && trackId !== "" ? `Envío ${trackId}` : "Sin envío"}
              </span>
            </div>
          );
        },
      }),
      columnHelper.accessor(
        (row) => row.receptor_razon_social || row.rut_receptor,
        {
          id: "receptor",
          header: () => (
            <ColumnSortHeader
              label={t("col.receptor")}
              sortKey="receptor_razon_social"
              activeSortBy={listFilters.sort_by}
              activeSortDir={listFilters.sort_dir}
              onSort={applyColumnSort}
            />
          ),
          cell: (info) => {
            const raw = info.getValue();
            const full =
              raw != null && raw !== ""
                ? String(raw)
                : "";
            const display =
              full === "" ? "—" : truncateChars(full, RECEPTOR_MAX_CHARS);
            return (
              <span
                className="inline-block max-w-full text-sm text-navy-700 dark:text-white"
                title={full || undefined}
              >
                {display}
              </span>
            );
          },
        }
      ),
      columnHelper.accessor("lifecycle_status", {
        header: () => (
          <ColumnSortHeader
            label={t("col.estado")}
            sortKey="lifecycle_status"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => {
          const raw = getDocumentLifecycleDisplayStatus(info.row.original);
          if (raw == null || raw === "") {
            return (
              <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
            );
          }
          const label = lifecycleStatusLabel(raw);
          const tooltip = lifecycleStatusTooltip(raw);
          const badge = (
            <Badge
              colorScheme={lifecycleStatusColorScheme(raw)}
              variant="subtle"
              textTransform="uppercase"
              fontSize="0.65rem"
              fontWeight="semibold"
            >
              {label}
            </Badge>
          );
          if (tooltip) {
            return (
              <Tooltip label={tooltip} hasArrow placement="top">
                {badge}
              </Tooltip>
            );
          }
          return badge;
        },
      }),
      columnHelper.accessor((row) => resolveEmissionDate(row), {
        id: "fch_emis",
        header: () => (
          <ColumnSortHeader
            label={t("col.fechaEmision")}
            sortKey="fch_emis"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm text-navy-700 dark:text-white">
            {formatEmissionDateTime(info.getValue(), info.row.original)}
          </span>
        ),
      }),
      columnHelper.accessor("monto_total", {
        header: () => (
          <ColumnSortHeader
            label={t("col.total")}
            sortKey="monto_total"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm font-medium text-navy-700 dark:text-white">
            {formatMoney(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor("sii_estado", {
        header: () => (
          <ColumnSortHeader
            label="SII"
            sortKey="sii_estado"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm text-navy-700 dark:text-white">
            {info.getValue() ?? "—"}
          </span>
        ),
      }),
      // Pasarela de pago del canal: la misma que sale en el PDF. Acá es para
      // el contador, que necesita verla sin abrir documento por documento.
      //
      // No se puede ordenar por esta columna: el dato vive dentro de `notes`
      // (un jsonb) y el backend no ordena por ahí. Un encabezado que parece
      // clickeable y no hace nada es peor que uno que no lo parece.
      columnHelper.accessor((row) => row?.notes?.metodo_pago_canal ?? null, {
        id: "metodo_pago",
        header: () => (
          <span className="inline-block text-left text-[11px] font-bold uppercase leading-snug tracking-wide text-gray-600 dark:text-gray-200">
            Canal y pago
          </span>
        ),
        enableSorting: false,
        cell: (info) => {
          const fila = info.row.original || {};
          const docId = fila.id;
          const pago = pagosPorDoc[String(docId)];
          // El canal va ARRIBA y es el titulo de la celda: primero por donde
          // se vendio, despues con que se pago. Sale de las referencias del
          // documento, asi que esta desde que se emitio, sin depender de
          // ninguna sincronizacion.
          const canal = canalDeVenta(fila, pago);
          // De lo mas preciso a lo mas general:
          //   1. el medio real, del pedido o de Mercado Pago
          //   2. la pasarela que la emision guardo en notes (la del PDF)
          //   3. la pasarela fija del canal, para los documentos viejos
          // El tercero evita un "Sin metodo de pago" en ventas que si se
          // sabe como se cobraron.
          const etiqueta = pago?.metodoPago
            || info.getValue()
            || (canal ? PASARELA_DE_CANAL[canal] : null);

          // Por donde entro la plata. Va ARRIBA, con el canal, porque es del
          // mismo nivel: canal y pasarela dicen DONDE, el renglon de abajo
          // dice CON QUE. Sin esto un pago por transferencia via Mercado Pago
          // se leia igual que una transferencia directa a la cuenta de la
          // empresa, que es otra plata y se concilia en otro lado.
          const pasarela = pasarelaAparte(pago?.pasarela, etiqueta);

          if (!etiqueta && !canal) {
            return <span className="text-sm text-gray-400">—</span>;
          }

          // Clickeable si hay algo que mostrar: el pago de Mercado Pago, el
          // pedido de Shopify o las cuotas. Las cuotas vienen del pedido, no
          // del pago, así que exigir el pago dejaba ese dato escondido.
          const hayDetalle = Boolean(pago?.pago || pago?.ordenUrl || pago?.cuotas);

          return (
            <div className="flex flex-col gap-0.5 leading-tight">
              {canal || pasarela ? (
                <span className="whitespace-nowrap text-sm text-navy-700 dark:text-white">
                  {canal ? <span className="font-bold">{NOMBRE_DE_CANAL[canal]}</span> : null}
                  {canal && pasarela ? (
                    <span className="text-gray-400"> · </span>
                  ) : null}
                  {pasarela ? (
                    <span className="text-xs text-gray-600 dark:text-gray-400">{pasarela}</span>
                  ) : null}
                </span>
              ) : null}
              {etiqueta && hayDetalle ? (
                <button
                  type="button"
                  onClick={() => setPagoAbierto({ ...pago, documentoId: docId })}
                  title="Ver el detalle del pago"
                  className={`whitespace-nowrap rounded text-left underline decoration-dotted underline-offset-2 hover:text-brand-700 dark:text-brand-400 ${
                    canal
                      ? "text-xs font-medium text-brand-600"
                      : "text-sm font-medium text-brand-600"
                  }`}
                >
                  {etiqueta}
                </button>
              ) : null}
              {etiqueta && !hayDetalle ? (
                <span
                  className={
                    canal
                      ? "whitespace-nowrap text-xs text-gray-600 dark:text-gray-400"
                      : "whitespace-nowrap text-sm text-navy-700 dark:text-white"
                  }
                >
                  {etiqueta}
                </span>
              ) : null}
              {/* Red de seguridad: un canal al que se le olvido la pasarela
                  en PASARELA_DE_CANAL. Hay un test que lo impide, pero si
                  pasa es mejor decirlo que dejar el renglon vacio. */}
              {!etiqueta && canal ? (
                <span className="whitespace-nowrap text-xs text-gray-400">
                  Sin método de pago
                </span>
              ) : null}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: () => (
          <span className="inline-block text-left text-[11px] font-bold uppercase leading-snug tracking-wide text-gray-600 dark:text-gray-200">
            Acciones
          </span>
        ),
        enableSorting: false,
        cell: ({ row }) => {
          const doc = row.original;
          const numericId = doc.id;
          if (numericId == null) {
            return (
              <span className="text-xs text-gray-400">—</span>
            );
          }
          const lifecycleStatus = getDocumentLifecycleDisplayStatus(doc);
          const canEnqueueUploadRetry =
            lifecycleStatus !== "reparado" &&
            (
              lifecycleStatus === "rejected" ||
              lifecycleStatus === "error" ||
              lifecycleStatus === "rendered" ||
              lifecycleStatus === "sent"
            );
          const canRepairWithCurrentCaf =
            lifecycleStatus === "rejected" || lifecycleStatus === "sent";
          const isHidden = Boolean(doc.hidden_at);
          const canHide = lifecycleStatus === "rejected" && !isHidden;
          const canRestore = isHidden;
          // Reenviar al cliente no altera el documento: no emite, no anula y no
          // gasta folios. Por eso el contador SÍ lo ve, aunque el resto del menú
          // (remitir al SII, reparar, ocultar) siga siendo solo para admin.
          //
          // Solo aparece en documentos que el SII ya aceptó. Uno rechazado no
          // tiene validez tributaria, y uno que aún no llega al SII no está
          // resuelto: si el cliente lo registra, después hay que deshacerlo de su
          // lado. Es la misma regla del envío automático, que sale únicamente
          // cuando el track queda "Aceptado". El backend lo bloquea igual.
          //
          // Se mira el estado REAL y no el de pantalla: este último devuelve
          // "reparado" para los DOS lados de una reparación, así que el
          // documento nuevo —el válido, el que el SII aceptó— quedaba
          // enmascarado y no se le podía mandar el folio nuevo al cliente.
          const estadoReal = String(doc.lifecycle_status ?? "").trim().toLowerCase();
          const estaAceptado =
            estadoReal === "accepted" || estadoReal === "accepted_with_observations";
          // El documento REEMPLAZADO no se manda nunca, aunque figure aceptado:
          // el que vale es su reemplazo, y mandar el folio viejo obliga después
          // a deshacerlo del lado del cliente.
          const refReparacion = resolveRepairReference(doc);
          const fueReemplazado = refReparacion?.kind === "reemplazado_por";
          // El documento que de verdad sirve. Los botones apuntan ahí: abrir el
          // corrupto es lo que nadie necesita, y era lo único que ofrecían.
          const idValido = fueReemplazado
            ? (refReparacion?.id ?? refReparacion?.uuid ?? null)
            : null;
          const canReenviar =
            Boolean(puedeReenviarDocumentos) && !isHidden && estaAceptado && !fueReemplazado;
          // Desde la fila del documento reparado se le manda al cliente el que
          // lo reparó. Es la razón por la que alguien abre esta fila: el
          // corrupto no se envía nunca, pero su reemplazo sí hay que mandarlo.
          const canReenviarReemplazo =
            Boolean(puedeReenviarDocumentos) && !isHidden && fueReemplazado && idValido != null;
          const showActionsMenu =
            canReenviar
            || canReenviarReemplazo
            || (puedeGestionarDocumentos
              && (canEnqueueUploadRetry || canRepairWithCurrentCaf || canHide || canRestore));
          const key = String(numericId);
          const startedAtTs = Number(reenqueueStartedAtById[key] || 0);
          const elapsedMs = startedAtTs > 0 ? Math.max(0, clockNow - startedAtTs) : 0;
          const inCooldown =
            startedAtTs > 0 && elapsedMs < REENQUEUE_COOLDOWN_MS;
          const remainingSeconds = inCooldown
            ? Math.ceil((REENQUEUE_COOLDOWN_MS - elapsedMs) / 1000)
            : 0;
          const isBusy = Boolean(enqueueBusyById[key]);
          const isRepairBusy = Boolean(repairBusyById[key]);
          const isHideBusy = Boolean(hideBusyById[key]);
          return (
            <div className="flex flex-nowrap gap-2 items-center">
              {/* Cuando el documento fue reemplazado, Ver y PDF llevan al que
                  lo reparó: ese es el que vale. El folio va escrito en el
                  botón para que nadie abra uno creyendo que es el otro, y
                  desde el detalle del reemplazo se puede volver al corrupto. */}
              {fueReemplazado && idValido != null ? (
                <>
                  <button
                    type="button"
                    title={`Abrir el documento folio ${refReparacion?.folio ?? idValido}, que reparó a este.`}
                    onClick={() => openDetail(idValido)}
                    className="inline-flex px-3 py-1.5 text-xs font-semibold text-white rounded-lg transition bg-brand-500 hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-300"
                  >
                    Ver {refReparacion?.folio ?? `#${idValido}`}
                  </button>
                  <button
                    type="button"
                    disabled={pdfReemplazoBusyId === String(idValido)}
                    title={`Abrir el PDF del documento folio ${refReparacion?.folio ?? idValido}, que reparó a este.`}
                    onClick={() => abrirPdfDeReemplazo(idValido)}
                    className="inline-flex px-3 py-1.5 text-xs font-semibold bg-white rounded-lg border border-tertiary-500 text-tertiary-600 hover:bg-tertiary-50 disabled:opacity-50 dark:border-tertiary-400 dark:bg-navy-800 dark:text-tertiary-300 dark:hover:bg-navy-700"
                  >
                    {pdfReemplazoBusyId === String(idValido)
                      ? "Abriendo…"
                      : `PDF ${refReparacion?.folio ?? ""}`.trim()}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => openDetail(numericId)}
                    className="inline-flex px-3 py-1.5 text-xs font-semibold text-white rounded-lg transition bg-brand-500 hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-300"
                  >
                    Ver
                  </button>
                  {doc.pdf_url ? (
                    <button
                      type="button"
                      onClick={() =>
                        openPdf(
                          doc.pdf_url,
                          doc.sii_track_id != null && doc.sii_track_id !== ""
                            ? doc.sii_track_id
                            : doc.folio
                        )
                      }
                      className="inline-flex px-3 py-1.5 text-xs font-semibold bg-white rounded-lg border border-tertiary-500 text-tertiary-600 hover:bg-tertiary-50 dark:border-tertiary-400 dark:bg-navy-800 dark:text-tertiary-300 dark:hover:bg-navy-700"
                    >
                      PDF
                    </button>
                  ) : null}
                </>
              )}
              {showActionsMenu ? (
                <div
                  className="relative"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    aria-label={t("col.acciones")}
                    aria-expanded={openActionsMenuId === key}
                    onClick={(e) => alternarMenuAcciones(key, e)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 transition hover:bg-gray-50 dark:border-white/20 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
                  >
                    <BsThreeDotsVertical className="h-4 w-4" />
                  </button>
                  {openActionsMenuId === key ? (
                    <div
                      className={`absolute right-0 z-30 w-52 rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg dark:border-white/10 dark:bg-navy-800 ${
                        menuHaciaArriba ? "bottom-9" : "top-9"
                      }`}
                    >
                      {canReenviarReemplazo ? (
                        <button
                          type="button"
                          disabled={reenvioReemplazoBusyId === String(idValido)}
                          onClick={() => {
                            setOpenActionsMenuId(null);
                            abrirReenvioDeReemplazo(idValido);
                          }}
                          className="block w-full px-3 py-2 text-left text-sm font-medium text-navy-700 transition hover:bg-gray-50 disabled:opacity-50 dark:text-white dark:hover:bg-navy-700"
                          title="Enviarle al cliente el PDF y el XML del documento que reparó a este."
                        >
                          {reenvioReemplazoBusyId === String(idValido)
                            ? "Cargando…"
                            : `Enviar al cliente folio ${refReparacion?.folio ?? idValido}`}
                        </button>
                      ) : null}
                      {canReenviar ? (
                        <button
                          type="button"
                          onClick={() => {
                            setOpenActionsMenuId(null);
                            setReenvioDoc(doc);
                          }}
                          className="block w-full px-3 py-2 text-left text-sm font-medium text-navy-700 transition hover:bg-gray-50 dark:text-white dark:hover:bg-navy-700"
                          title="Reenviar el PDF y el XML al correo del cliente."
                        >
                          Reenviar al cliente
                        </button>
                      ) : null}
                      {puedeGestionarDocumentos ? (
  <>
                        {/* SENT con trackId = llegó al SII y no sabemos qué
                            dijo. Volver a preguntar no altera nada. */}
                        {estadoReal === "sent" && hasSiiTrack(doc) ? (
                          <button
                            type="button"
                            disabled={Boolean(trackingBusyById[key])}
                            onClick={() => {
                              setOpenActionsMenuId(null);
                              handleConsultarEstado(numericId);
                            }}
                            className="block w-full px-3 py-2 text-left text-sm font-medium text-navy-700 transition hover:bg-gray-50 disabled:opacity-50 dark:text-white dark:hover:bg-navy-700"
                            title="Volver a preguntarle al SII en qué quedó este documento. No lo reenvía ni lo altera."
                          >
                            {trackingBusyById[key] ? "Consultando…" : "Consultar estado al SII"}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => {
                            if (!canEnqueueUploadRetry || isBusy || inCooldown) return;
                            setOpenActionsMenuId(null);
                            handleReenqueuePipeline(numericId);
                          }}
                          disabled={!canEnqueueUploadRetry || isBusy || inCooldown}
                          className="block w-full px-3 py-2 text-left text-sm font-medium text-navy-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white dark:hover:bg-navy-700"
                          title={
                            inCooldown
                              ? `Espera ${remainingSeconds}s para volver a remitir este DTE.`
                              : "Reenviar al SII."
                          }
                        >
                          {isBusy ? "Remitiendo…" : inCooldown ? `Remitido ${formatElapsedTimer(elapsedMs)}` : "Remitir al SII"}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (!canRepairWithCurrentCaf || isRepairBusy) return;
                            setOpenActionsMenuId(null);
                            handleRepairWithCurrentCaf(doc);
                          }}
                          disabled={!canRepairWithCurrentCaf || isRepairBusy}
                          className="block w-full px-3 py-2 text-left text-sm font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-300 dark:hover:bg-navy-700"
                          title="Rehacer el documento con el CAF activo y un folio nuevo."
                        >
                          {isRepairBusy ? "Reparando…" : "Reparar con otro CAF"}
                        </button>
                        {canHide ? (
                          <button
                            type="button"
                            onClick={() => {
                              if (!canHide || isHideBusy) return;
                              setOpenActionsMenuId(null);
                              handleToggleHideDocument(doc, true);
                            }}
                            disabled={!canHide || isHideBusy}
                            className="block w-full border-t border-gray-100 px-3 py-2 text-left text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-red-400 dark:hover:bg-navy-700"
                            title="Ocultar (desactivar) este DTE rechazado. Reversible desde 'Ver ocultos'."
                          >
                            {isHideBusy ? "Ocultando…" : "Ocultar"}
                          </button>
                        ) : null}
                        {canRestore ? (
                          <button
                            type="button"
                            onClick={() => {
                              if (!canRestore || isHideBusy) return;
                              setOpenActionsMenuId(null);
                              handleToggleHideDocument(doc, false);
                            }}
                            disabled={!canRestore || isHideBusy}
                            className="block w-full border-t border-gray-100 px-3 py-2 text-left text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-emerald-400 dark:hover:bg-navy-700"
                            title="Restaurar este DTE oculto al listado."
                          >
                            {isHideBusy ? "Restaurando…" : "Restaurar"}
                          </button>
                        ) : null}
  </>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        },
      }),
    ],
    [
      applyColumnSort,
      listFilters.sort_by,
      listFilters.sort_dir,
      openDetail,
      openPdf,
      abrirPdfDeReemplazo,
      abrirReenvioDeReemplazo,
      // Sin estos dos, los textos "Abriendo…" y "Cargando…" se quedarían
      // congelados: las columnas no se volverían a calcular al cambiar.
      pdfReemplazoBusyId,
      reenvioReemplazoBusyId,
      handleRepairWithCurrentCaf,
      handleReenqueuePipeline,
      handleToggleHideDocument,
      enqueueBusyById,
      repairBusyById,
      hideBusyById,
      reenqueueStartedAtById,
      clockNow,
      openActionsMenuId,
      alternarMenuAcciones,
      menuHaciaArriba,
      handleConsultarEstado,
      trackingBusyById,
      puedeReenviarDocumentos,
      puedeGestionarDocumentos,
      // Sin esto, la columna de método de pago se quedaría en "—": los pagos
      // llegan después del listado y las columnas no se recalcularían.
      pagosPorDoc,
      t,
    ]
  );

  const table = useReactTable({
    data,
    columns,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id ?? row.document_uuid),
  });

  const showBlockingLoading = loading && data.length === 0;
  const pageSize = Math.min(
    200,
    Math.max(1, Number(listFilters.limit) || 100)
  );
  const statusSummary = React.useMemo(() => {
    const base = {
      total: data.length,
      accepted: 0,
      rejected: 0,
      repaired: 0,
      error: 0,
      withPdf: 0,
      withTrack: 0,
    };
    for (const row of data) {
      const status = getDocumentLifecycleDisplayStatus(row);
      // Reparo = "Aceptado con Reparos": documento valido, se cuenta como aceptado.
      if (status === "accepted" || status === "accepted_with_observations") base.accepted += 1;
      if (status === "rejected") base.rejected += 1;
      if (status === "reparado") base.repaired += 1;
      if (status === "error") base.error += 1;
      if (hasPdfDocument(row)) base.withPdf += 1;
      if (hasSiiTrack(row)) base.withTrack += 1;
    }
    return base;
  }, [data]);

  return (
    <>
    <Card extra="w-full px-6 pb-6 sm:overflow-x-auto">
      <div className="flex relative flex-col gap-3 pt-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            {t("emitidos.tituloTarjeta")}
          </div>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {t("emitidos.descripcion")}
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              onClick={() => openDocumentsTab(false)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition ${
                !repairedOnly
                  ? "border-brand-500 bg-brand-500 text-white dark:border-brand-400 dark:bg-brand-400"
                  : "border-gray-200 bg-white text-navy-700 hover:bg-gray-50 dark:border-white/15 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
              }`}
            >
              DTE emitidos
            </button>
            <button
              type="button"
              onClick={() => openDocumentsTab(true)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition ${
                repairedOnly
                  ? "border-violet-500 bg-violet-500 text-white dark:border-violet-400 dark:bg-violet-400"
                  : "border-gray-200 bg-white text-navy-700 hover:bg-gray-50 dark:border-white/15 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
              }`}
            >
              Reparados
            </button>
          </div>
        </div>
        <div className="flex flex-col flex-shrink-0 gap-2 items-stretch sm:items-end">
          <div className="flex flex-col gap-2 w-full sm:w-auto sm:flex-row sm:justify-end">
            {/* Salida de emergencia, NO el arreglo: lo que corresponde es que
                el barredor los tome solo. Sirve para no quedarse esperando
                cuando ya hay documentos atascados. */}
            {puedeGestionarDocumentos && documentosSinVeredicto.length > 0 ? (
              <button
                type="button"
                onClick={handleConsultarEstadoLote}
                disabled={Boolean(consultaLoteProgreso) || loading}
                title="Vuelve a preguntarle al SII por los documentos de esta página que quedaron en SENT. No los reenvía ni los altera."
                className="w-full rounded-xl border border-amber-400 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-800 transition duration-200 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-400/40 dark:bg-amber-400/10 dark:text-amber-200 sm:w-auto"
              >
                {consultaLoteProgreso
                  ? `Consultando ${consultaLoteProgreso.hechos}/${consultaLoteProgreso.total}…`
                  : `Consultar estado al SII (${documentosSinVeredicto.length})`}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => {
                setShowHidden((v) => !v);
                setPage(1);
              }}
              disabled={loading}
              aria-pressed={showHidden}
              className={`px-4 py-2.5 w-full text-sm font-medium rounded-xl border transition duration-200 linear disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto ${
                showHidden
                  ? "border-amber-500 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-400 dark:bg-navy-700 dark:text-amber-200"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
              }`}
            >
              {showHidden ? t("accion.verActivos") : t("accion.verOcultos")}
            </button>
            <button
              type="button"
              onClick={handleExportCsv}
              disabled={loading || exporting}
              className="px-4 py-2.5 w-full text-sm font-medium bg-white rounded-xl border transition duration-200 linear border-tertiary-500 text-tertiary-700 hover:bg-tertiary-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-tertiary-400 dark:bg-navy-800 dark:text-tertiary-200 dark:hover:bg-navy-700 sm:w-auto"
            >
              {exporting ? "Exportando…" : repairedOnly ? "Exportar Excel" : "Exportar CSV"}
            </button>
            <button
              type="button"
              onClick={handleExportDetailXlsx}
              disabled={loading || exporting}
              title="Excel con una fila por producto de cada DTE (detalle)"
              className="px-4 py-2.5 w-full text-sm font-medium bg-white rounded-xl border transition duration-200 linear border-emerald-500 text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-400 dark:bg-navy-800 dark:text-emerald-200 dark:hover:bg-navy-700 sm:w-auto"
            >
              {exporting ? "Exportando…" : "Exportar Excel (detalle)"}
            </button>
            <button
              type="button"
              onClick={() => fetchDocuments()}
              disabled={loading}
              className="px-4 py-2.5 w-full text-sm font-medium text-white rounded-xl transition duration-200 linear bg-brand-500 hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:bg-brand-200 sm:w-auto"
            >
              {loading ? t("accion.actualizando") : t("accion.actualizar")}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-4 mb-4 md:grid-cols-6">
        <div className="px-3 py-2 bg-white rounded-xl border border-gray-200 dark:border-white/10 dark:bg-navy-900/60">
          <p className="text-[11px] uppercase text-gray-500 dark:text-gray-400">
            {t("filtro.enPagina")}
          </p>
          <p className="text-lg font-bold text-navy-700 dark:text-white">
            {statusSummary.total}
          </p>
        </div>
        <div className="px-3 py-2 bg-emerald-50 rounded-xl border border-emerald-300 dark:border-emerald-500/40 dark:bg-emerald-500/10">
          <p className="text-[11px] uppercase text-emerald-700 dark:text-emerald-300">
            Aceptados
          </p>
          <p className="text-lg font-bold text-emerald-800 dark:text-emerald-100">
            {statusSummary.accepted}
          </p>
        </div>
        <div className="px-3 py-2 bg-pink-50 rounded-xl border border-pink-300 dark:border-pink-500/40 dark:bg-pink-500/10">
          <p className="text-[11px] uppercase text-pink-700 dark:text-pink-300">
            Rechazados
          </p>
          <p className="text-lg font-bold text-pink-800 dark:text-pink-100">
            {statusSummary.rejected}
          </p>
        </div>
        <div className="px-3 py-2 bg-violet-50 rounded-xl border border-violet-300 dark:border-violet-500/40 dark:bg-violet-500/10">
          <p className="text-[11px] uppercase text-violet-700 dark:text-violet-300">
            Reparados
          </p>
          <p className="text-lg font-bold text-violet-800 dark:text-violet-100">
            {statusSummary.repaired}
          </p>
        </div>
        <div className="px-3 py-2 bg-red-50 rounded-xl border border-red-300 dark:border-red-500/40 dark:bg-red-500/10">
          <p className="text-[11px] uppercase text-red-700 dark:text-red-300">
            Con error
          </p>
          <p className="text-lg font-bold text-red-800 dark:text-red-100">
            {statusSummary.error}
          </p>
        </div>
        <div className="px-3 py-2 bg-blue-50 rounded-xl border border-blue-300 dark:border-blue-500/40 dark:bg-blue-500/10">
          <p className="text-[11px] uppercase text-blue-700 dark:text-blue-300">
            Con PDF
          </p>
          <p className="text-lg font-bold text-blue-800 dark:text-blue-100">
            {statusSummary.withPdf}
          </p>
        </div>
      </div>

      {showBlockingLoading ? (
        <p className="mt-8 text-sm text-gray-600 dark:text-gray-400">
          Cargando documentos…
        </p>
      ) : null}

      {error ? (
        <p
          className="px-4 py-3 mt-8 text-sm text-red-800 bg-red-50 rounded-lg border border-red-400/40 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-200"
          role="alert"
        >
          {error}
        </p>
      ) : null}

      {exportMessage ? (
        <p
          className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
            exportMessage.startsWith("Archivo generado")
              ? "border-emerald-400/40 bg-emerald-50 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-950/40 dark:text-emerald-100"
              : "border-amber-400/40 bg-amber-50 text-amber-950 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100"
          }`}
          role="status"
        >
          {exportMessage}
        </p>
      ) : null}

      {enqueueMessage ? (
        <p
          className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
            enqueueMessage.startsWith("Upload reencolado") ||
            enqueueMessage.startsWith("Documento rencolado")
              ? "border-emerald-400/40 bg-emerald-50 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-950/40 dark:text-emerald-100"
              : "border-amber-400/40 bg-amber-50 text-amber-950 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100"
          }`}
          role="status"
        >
          {enqueueMessage}
        </p>
      ) : null}

      {!error && !showBlockingLoading ? (
        <>
          <div className="p-4 mt-6 rounded-xl border border-gray-200 bg-lightPrimary/50 dark:border-white/10 dark:bg-navy-900/30">
            <div className="text-xs font-bold tracking-wide text-gray-600 uppercase dark:text-gray-400">
              {t("filtro.titulo")}
            </div>
            <div className="mt-3">
              <FiltroRangoFechas
                desde={dateDesde}
                hasta={dateHasta}
                etiquetaDesde={t("filtro.emisionDesde")}
                etiquetaHasta={t("filtro.emisionHasta")}
                onChange={(d, h) => {
                  setDateDesde(d);
                  setDateHasta(h);
                  setPage(1);
                }}
              />
            </div>
            <div className="grid grid-cols-1 gap-3 mt-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">

              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                {t("filtro.modo")}
                <select
                  value={listFilters.modo}
                  onChange={(e) =>
                    setListFilters((f) => ({ ...f, modo: e.target.value }))
                  }
                  disabled
                  className="px-3 py-2 text-sm bg-white rounded-lg border border-gray-200 text-navy-800 dark:border-white/15 dark:bg-navy-800 dark:text-white"
                >
                  {MODO_OPTIONS.map((o) => (
                    <option key={o.value || "all"} value={o.value}>
                      {t(o.label)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                {t("filtro.estadoCiclo")}
                <select
                  value={listFilters.lifecycle_status}
                  onChange={(e) =>
                    setListFilters((f) => ({
                      ...f,
                      lifecycle_status: e.target.value,
                    }))
                  }
                  disabled={repairedOnly}
                  className="px-3 py-2 text-sm bg-white rounded-lg border border-gray-200 text-navy-800 dark:border-white/15 dark:bg-navy-800 dark:text-white"
                >
                  {LIFECYCLE_OPTIONS.map((o) => (
                    <option key={o.value || "all"} value={o.value}>
                      {t(o.label)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                {t("filtro.tipoDte")}
                <select
                  value={listFilters.tipo_dte}
                  onChange={(e) =>
                    setListFilters((f) => ({ ...f, tipo_dte: e.target.value }))
                  }
                  className="px-3 py-2 text-sm bg-white rounded-lg border border-gray-200 text-navy-800 dark:border-white/15 dark:bg-navy-800 dark:text-white"
                >
                  <option value="">{t("filtro.cualquiera")}</option>
                  {DTE_TIPO_FILTER_SELECT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                {t("filtro.rutEmisor")}
                <input
                  type="text"
                  placeholder={selectedRutEmisor || "Sin emisor activo"}
                  value={listFilters.rut_emisor}
                  onChange={(e) =>
                    setListFilters((f) => ({
                      ...f,
                      rut_emisor: e.target.value,
                    }))
                  }
                  disabled
                  className="px-3 py-2 text-sm bg-white rounded-lg border border-gray-200 text-navy-800 placeholder:text-gray-400 dark:border-white/15 dark:bg-navy-800 dark:text-white"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                {t("filtro.limite")}
                <select
                  value={listFilters.limit}
                  onChange={(e) =>
                    setListFilters((f) => ({
                      ...f,
                      limit: Number(e.target.value),
                    }))
                  }
                  className="px-3 py-2 text-sm bg-white rounded-lg border border-gray-200 text-navy-800 dark:border-white/15 dark:bg-navy-800 dark:text-white"
                >
                  {LIMIT_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                Máx. filas export CSV
                <select
                  value={exportLimit}
                  onChange={(e) => setExportLimit(Number(e.target.value))}
                  className="px-3 py-2 text-sm bg-white rounded-lg border border-gray-200 text-navy-800 dark:border-white/15 dark:bg-navy-800 dark:text-white"
                >
                  {EXPORT_LIMIT_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n.toLocaleString("es-CL")}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex flex-col gap-2 justify-end">
                <button
                  type="button"
                  onClick={resetListFiltersAndSearch}
                  className="px-3 py-2 text-sm font-medium bg-white rounded-lg border border-gray-300 text-navy-700 hover:bg-gray-50 dark:border-white/20 dark:bg-navy-800 dark:text-gray-200 dark:hover:bg-navy-700"
                >
                  Limpiar filtros y búsqueda
                </button>
              </div>
            </div>
            <div className="pt-4 mt-4 border-t border-gray-200 dark:border-white/10">
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                Buscar en base de datos (track, folio, RUT, receptor, UUID,
                estados…). Parámetro{" "}
                <code className="text-[10px]">q</code>.
                <input
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Escribe y espera ~0,4 s o cambia de página…"
                  className="px-3 py-2.5 w-full text-sm bg-white rounded-lg border border-gray-200 text-navy-800 placeholder:text-gray-400 dark:border-white/15 dark:bg-navy-800 dark:text-white sm:max-w-xl"
                  autoComplete="off"
                />
              </label>
            </div>
          </div>

          <div className="overflow-x-auto relative mt-6">
            {loading && data.length > 0 ? (
              <div className="pointer-events-none absolute inset-0 z-[1] flex justify-center bg-white/40 pt-6 dark:bg-navy-950/50">
                <span className="px-3 py-1.5 text-xs font-medium bg-white rounded-lg border border-gray-200 shadow-sm h-fit dark:border-white/10 dark:bg-navy-800">
                  Actualizando…
                </span>
              </div>
            ) : null}
          <table className="w-full min-w-[960px]">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="border-b border-gray-200 bg-gray-50/90 dark:border-white/10 dark:bg-navy-900/50"
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className="px-1 pt-2.5 pb-2.5 align-middle cursor-default select-none text-start first:pl-0 last:pr-0"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="py-8 text-sm text-center text-gray-500 dark:text-gray-400"
                  >
                    {listMeta.total === 0
                      ? "No hay documentos con los filtros y búsqueda seleccionados."
                      : "Sin filas en esta página."}
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-gray-100 dark:border-white/5"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-1 py-3 pr-3 align-middle sm:pr-4">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

          <ServerTablePagination
            page={page}
            totalPages={listMeta.total_pages}
            total={listMeta.total}
            pageSize={pageSize}
            noun="documentos"
            subtext="Ordenación en servidor (sort_by / sort_dir)."
            onPageChange={setPage}
          />
        </>
      ) : null}
    </Card>

      <ReenviarCorreoModal

        isOpen={Boolean(reenvioDoc)}

        onClose={() => setReenvioDoc(null)}

        documento={reenvioDoc}

        enviando={reenvioBusy}

        onConfirmar={handleReenviarCorreo}

      />


      <AppModal
        isOpen={detailOpen}
        onClose={closeDetail}
        title={`Documento DTE${detailId != null ? ` · ID ${detailId}` : ""}`}
        size="xl"
        footer={
          <Button colorScheme="brand" variant="solid" onClick={closeDetail}>
            Cerrar
          </Button>
        }
      >
        {detailLoading ? (
          <div className="flex justify-center py-8">
            <Spinner color="brand.500" size="lg" thickness="4px" />
          </div>
        ) : null}
        {detailError ? (
          <p
            className="px-3 py-2 text-sm text-red-800 bg-red-50 rounded-lg border border-red-400/40 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-200"
            role="alert"
          >
            {detailError}
          </p>
        ) : null}
        {!detailLoading && !detailError && detailItem ? (
          <DteDocumentDetailFields
            item={detailItem}
            onOpenDocument={openDetail}
          />
        ) : null}
      </AppModal>

      <PdfModal
        isOpen={pdfOpen}
        onClose={closePdf}
        src={pdfUrl}
        title={pdfTitle}
      />

      {/* Detalle del pago de Mercado Pago, al hacer clic en el método. */}
      <AppModal
        isOpen={Boolean(pagoAbierto)}
        onClose={() => setPagoAbierto(null)}
        title="Pago en Mercado Pago"
      >
        {pagoAbierto ? <DetallePago info={pagoAbierto} /> : null}
      </AppModal>
    </>
  );
}

/**
 * Con qué regla se cruzó el pago, en castellano.
 *
 * Acá llegaba el nombre interno tal cual, así que una venta de MercadoLibre
 * se mostraba como "puente_shopify": ni se entiende ni es cierto. Lo que
 * importa que se lea es si el cruce fue por identificador —exacto— o deducido
 * por monto y fecha, que es lo que el contador tiene que revisar.
 */
const COMO_SE_CRUZO = {
  referencia_externa: "Por la referencia externa del documento (exacto)",
  rut_monto_fecha: "Deducido por RUT, monto y fecha",
  monto_fecha: "Deducido por monto y fecha",
  manual: "Vinculado a mano",
};

/**
 * EL CANAL SE LEE DEL PEDIDO, NO DEL NOMBRE DE LA REGLA.
 *
 * Los pagos conciliados ANTES de que la regla llevara el canal quedaron
 * guardados como `puente_shopify` aunque fueran de MercadoLibre, y no se
 * arreglan solos: la conciliación nunca vuelve a mirar un pago ya conciliado
 * —busca `pendiente`, `sin_documento` y `ambiguo`— así que ese nombre se
 * queda ahí para siempre.
 *
 * Por eso la frase se arma con el canal del pedido, que sí es confiable, y
 * del nombre de la regla se usa solo lo que de verdad dice: que el cruce fue
 * por el puente. Así los 330 pagos viejos se leen bien sin tocar la base.
 */
function textoComoSeCruzo(regla, canal) {
  const clave = String(regla || "").trim();
  if (!clave) return "—";

  if (clave.startsWith("puente")) {
    return canal
      ? `Por el id del pago en el pedido de ${canal} (exacto)`
      : "Por el id del pago en el pedido (exacto)";
  }

  // Una regla nueva que el frontend no conozca se muestra igual, en vez de
  // desaparecer: preferible el nombre interno a un "—" que miente.
  return COMO_SE_CRUZO[clave] || clave;
}

/** Lo que se ve al hacer clic en el método de pago de una fila. */
function DetallePago({ info }) {
  const pago = info?.pago || {};
  // El canal del pedido, que es el dato confiable. Se usa para rotular el
  // pedido y para armar la frase de "Cómo se cruzó".
  const nombreCanal = NOMBRE_DE_CANAL[info?.canal] || null;
  const fecha = pago.fechaAprobacion
    ? new Date(pago.fechaAprobacion).toLocaleString("es-CL")
    : "—";
  const monto = pago.monto == null
    ? "—"
    : new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: pago.moneda || "CLP",
      maximumFractionDigits: 0,
    }).format(pago.monto);

  // Cuotas: cuántas y cuánto vale cada una. Es una división simple, pero es
  // la pregunta que se hace cualquiera al ver una venta en cuotas.
  const cuotas = Number(info?.cuotas) || null;
  const montoCobrado = info?.montoCobrado == null ? null : Number(info.montoCobrado);
  const pesos = (n) => new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: pago.moneda || "CLP",
    maximumFractionDigits: 0,
  }).format(n);
  const textoCuotas = cuotas && cuotas > 1 && montoCobrado
    ? `${cuotas} cuotas de ${pesos(Math.round(montoCobrado / cuotas))}`
    : cuotas === 1
      ? "Sin cuotas (1 pago)"
      : null;

  // Las filas de Mercado Pago solo se arman si hay pago: si no, serían cinco
  // guiones seguidos que no dicen nada.
  const hayPagoMp = Boolean(pago.mpPaymentId);

  const filas = [
    ["Pasarela", info.pasarela || "—"],
    ["Método de pago", info.metodoPago || "—"],
    ...(textoCuotas ? [["Cuotas", textoCuotas]] : []),
    ...(hayPagoMp ? [
      ["Monto", monto],
      ["Fecha de acreditación", fecha],
      ["Estado en Mercado Pago", pago.estado || "—"],
      ["Detalle del estado", pago.estadoDetalle || "—"],
      ["ID del pago", pago.mpPaymentId],
    ] : [
      ["Monto cobrado", montoCobrado == null ? "—" : pesos(montoCobrado)],
    ]),
    // El enlace solo existe para Shopify, que es de donde sale el dominio de
    // la tienda. Un pedido de otro canal, o una venta de mostrador, muestra
    // el texto a secas. El rótulo nombra el canal del pedido: decir "Pedido
    // de Shopify" en una venta de MercadoLibre confunde al que concilia.
    [
      `Pedido de ${nombreCanal || "canal"}`,
      info.ordenUrl ? (
        <a
          href={info.ordenUrl}
          target="_blank"
          rel="noreferrer"
          className="text-brand-600 underline decoration-dotted underline-offset-2 hover:text-brand-700 dark:text-brand-400"
        >
          {info.ordenNumero} ↗
        </a>
      ) : (
        info.ordenNumero || "—"
      ),
    ],
    ...(hayPagoMp ? [
      ["Conciliación", pago.conciliacion?.estado || "—"],
      // Si fue por identificador o por monto y fecha: la diferencia entre un
      // cruce exacto y uno deducido, que es lo que hay que revisar.
      ["Cómo se cruzó", textoComoSeCruzo(pago.conciliacion?.regla, nombreCanal)],
    ] : []),
  ];

  return (
    <div className="space-y-2">
      <dl className="divide-y divide-gray-100 dark:divide-white/10">
        {filas.map(([etiqueta, valor]) => (
          <div key={etiqueta} className="flex justify-between gap-4 py-2">
            <dt className="text-sm text-gray-600 dark:text-gray-400">{etiqueta}</dt>
            <dd className="text-right text-sm font-medium text-navy-700 dark:text-white">
              {valor}
            </dd>
          </div>
        ))}
      </dl>
      <p className="pt-1 text-xs text-gray-500">
        {hayPagoMp
          ? "Los datos vienen de Mercado Pago y de la orden de Shopify."
          : "Los datos vienen de la orden de Shopify. Sincronizá Mercado Pago para ver si el pago se acreditó."}{" "}
        El documento tributario no cambia.
      </p>
    </div>
  );
}
